import { SnCamera, SnCameraStatus } from '@lib/api/entities';
import { FsAdvancedMarker } from '@lib/geo/google/map';
import { SnLonLat } from '@lib/geo/helpers';

import LatLngLiteral = google.maps.LatLngLiteral;

export class FsCameraAdvancedMarker extends FsAdvancedMarker {
  get isUav(): boolean {
    return this.camera?.isUav ?? false;
  }

  public id: string;

  private _animationId: number | null = null;
  private _animationLastPosition: LatLngLiteral | null = null;
  private _destination: google.maps.LatLng;
  private _distanceToDestination: number;
  private _deltaLat: number;
  private _deltaLng: number;
  private _expectedMoves: number;
  private _actualMoves: number;
  private _followMode: boolean;
  public _timestamp?: number;

  constructor(public camera?: SnCamera, public cluster?: any, public selected?: boolean) {
    super(
      {
        position: cluster
          ? new google.maps.LatLng(cluster.geometry.coordinates[1], cluster.geometry.coordinates[0])
          : new google.maps.LatLng(
              camera?.geometry?.coordinates[1],
              camera?.geometry?.coordinates[0],
            ),
        title: camera ? camera.name : null,
        content: document.createElement('div'),
        collisionBehavior: google.maps.CollisionBehavior.REQUIRED,
      },
      null,
      cluster,
    );
    this.id = `camera-${camera?.id}`;
    const content: any = this.getMarker().content;
    const imgIcon = cluster ? 'CameraUp' : this._getIcon().fontIcon;
    const markerType = cluster ? 'fs__advanced-camera-cluster' : 'fs__camera-advanced-marker';
    content.classList.add(markerType);
    content.innerHTML = `
      <img class="fs__camera-advanced-icon" src="/assets/fusus-icons/PNG/${imgIcon}.png"/>
    `;
    if (this.cluster) {
      this.createBadge();
    }
    if (this.selected) {
      this.createSelectedBadge();
    }
    const img = content.querySelector('.fs__camera-advanced-icon');
    if (camera && !this.isUav) {
      img.style.transform = `rotate(${camera.heading - 90}deg)`;
    }
    if (this.isUav) {
      content.classList.add('fs__camera-uav-marker');
      this.setBadgeStatus(this._getIcon().color, {
        bottom: '-15px',
        right: '-3px',
      });
      content.style.border = `1px solid ${this._getIcon().color}`;
    }
    content.classList.add('fs__center-marker');
  }

  get icon(): string {
    return this._getIcon();
  }

  public setDestinationWithAnimation(
    endLatLng: google.maps.LatLng,
    durationMs: number = 1000,
  ): void {
    if (this._animationId) {
      cancelAnimationFrame(this._animationId);
      this._animationId = null;
    }
    this._animationLastPosition = this.getMarker().position as unknown as LatLngLiteral;
    const lerp = (start: number, end: number, t: number): number => {
      return start + (end - start) * t;
    };
    const startTime = Date.now();
    const step = (): void => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / durationMs, 1);
      const lat = lerp(this._animationLastPosition.lat, endLatLng.lat(), progress);
      const lng = lerp(this._animationLastPosition.lng, endLatLng.lng(), progress);
      const newPosition = new google.maps.LatLng(lat, lng);
      if (this._destination) {
        this.getMarker().position = this._destination;
      }
      this._destination = newPosition;
      if (progress >= 1) {
        this._destination = endLatLng;
        this._animationId = null;
      } else {
        this._animationId = requestAnimationFrame(step);
      }
    };
    this._animationId = requestAnimationFrame(step);
  }

  public setDestination(
    latLng: google.maps.LatLng,
    timestamp: number | undefined = undefined,
  ): void {
    // if setting a new, non-null timestamp and the time is in the past, ignore
    if (!!timestamp && timestamp < this._timestamp) {
      return;
    }
    this._timestamp = timestamp;

    if (this._destination) {
      this.getMarker().position = this._destination;
    }
    this._destination = latLng;
  }

  public setDeltas(seconds: number): void {
    const destination = this._destination;
    const current = this.getLonLat();
    this._expectedMoves = 10 * seconds;
    this._actualMoves = 0;
    this._deltaLat = (destination.lat() - current.lat) / this._expectedMoves;
    this._deltaLng = (destination.lng() - current.lon) / this._expectedMoves;
  }

  public move(): void {
    if (this._actualMoves >= this._expectedMoves) {
      const lonLat = new SnLonLat(this._destination.lng(), this._destination.lat());
      this.setLonLat(lonLat);
      if (this._followMode) {
        this.map.panTo(lonLat);
      }
      return;
    }
    this._actualMoves++;
    const current = this.getLonLat();
    const destination = this._destination;
    const meters2Miles = 0.000621371192;
    this._distanceToDestination =
      Math.round(
        google.maps.geometry.spherical.computeDistanceBetween(
          { lat: current.lat, lng: current.lon },
          this._destination,
        ) *
          meters2Miles *
          10,
      ) / 10;
    if (!this.map?.contains(current)) {
      this._actualMoves = this._expectedMoves;
      const lonLat = new SnLonLat(destination.lng(), destination.lat());
      this.setLonLat(lonLat);
    }
    let newLat = current.lat + this._deltaLat;
    if (current.lat === destination.lat()) {
      newLat = destination.lat();
    }
    let newLng = current.lon + this._deltaLng;
    if (current.lon === destination.lng()) {
      newLng = destination.lng();
    }
    const newLonLat = new SnLonLat(newLng, newLat);
    this.setLonLat(newLonLat);
    if (this._followMode) {
      this.map.panTo(newLonLat);
    }
    if (!this.camera?.isUav && this._distanceToDestination > 0.1) {
      this.moveToDestination();
    }
  }

  public moveToDestination(): void {
    if (!this._destination) {
      return;
    }
    this.setLonLat(new SnLonLat(this._destination.lng(), this._destination.lat()));
  }

  public setFollowMode(followMode: boolean): void {
    this._followMode = followMode;
  }

  public getFollowMode(): boolean {
    return this._followMode;
  }

  public updateDroneStatus(): void {
    if (this.isUav) {
      const content: any = this.getMarker().content;
      this.setBadgeStatus(this._getIcon().color, {
        bottom: '-15px',
        right: '-3px',
      });
      content.style.border = `1px solid ${this._getIcon().color}`;
    }
  }

  private _getIcon(): any {
    const icon: any = {
      fontSet: 'fs',
    };
    if (this.cluster) {
      icon.fontIcon = 'CameraExists';
      return icon;
    }
    if (this.isUav) {
      icon.fontIcon = 'Icon_UAV';
      icon.color = this._getStatusColor();
      return icon;
    }
    let status = 'Exists';
    const ptz = this.camera.type === 'PTZ';
    if (this.camera.isParentCamera) {
      icon.fontIcon = 'quad_camera_full';
      icon.color = this._getStatusColor();
      return icon;
    }
    switch (this.camera.status) {
      case SnCameraStatus.Up:
        status = 'Up';
        break;
      case SnCameraStatus.Down:
        status = 'Down';
        break;
      case SnCameraStatus.Unmanaged:
        icon.fontIcon = 'CameraUnmanaged';
        return icon;
    }
    if (ptz) {
      icon.fontIcon = this.camera.isAiEnabled ? `Camera${status}_AI_PTZ` : `Camera${status}_PTZ`;
      return icon;
    }
    icon.fontIcon = this.camera.isAiEnabled ? `Camera${status}_AI` : `Icon_Camera${status}`;
    return icon;
  }

  private _getStatusColor(): string {
    return this.camera.status === SnCameraStatus.Up
      ? '#67BD4D'
      : this.camera.status === SnCameraStatus.Down
      ? '#FF3535'
      : this.camera.status === SnCameraStatus.Unmanaged
      ? '#FFB028'
      : 'rgb(137, 137, 137)';
  }
}
